import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { readPaperSize, subscribePaperSize } from '../../utils/paperSize';
import { isMissingPageSetupColumn } from '../../utils/pageSetup';
import PageSetupDialog from './PageSetupDialog';

/**
 * Page setup context: hands the open document's or template's page_setup (and the live-field
 * context) to everything inside the editor, and owns the Page setup dialog.
 *
 *   <PageSetupProvider
 *     pageSetup={pageSetup}               // the row's page_setup (null = defaults)
 *     onChange={(next) => { setPageSetup(next); markDirty(); }}
 *     fieldContext={{ title, customer, project, date }}
 *     templateId={documentData.template_id}  // documents only: "From template" + Reset
 *     target="document"                    // or 'template'
 *     open={open} onOpenChange={setOpen}   // optional: control the dialog from the host's menu
 *   >
 *     ...editor...
 *   </PageSetupProvider>
 *
 *   const ctx = usePageSetup();   // null outside a provider
 *   ctx.pageSetup, ctx.fieldContext, ctx.openPageSetup()
 *
 * The toolbar's More menu shows "Page setup…" whenever a provider is present.
 */
export const PageSetupContext = createContext(null);

/**
 * Read a template's name and current page_setup.
 * @param {string} templateId - Template id.
 * @returns {Promise<{name: string, pageSetup: object|null}|null>} Template info, or null when unavailable.
 */
export async function loadTemplatePageSetup(templateId) {
  if (!templateId) return null;
  // Loaded on demand so the editor toolbar (which reads this context) does not need the client.
  const { supabase } = await import('../../lib/supabase');
  const first = await supabase.from('templates').select('name, page_setup').eq('id', templateId).maybeSingle();
  if (!first.error) return first.data ? { name: first.data.name || '', pageSetup: first.data.page_setup ?? null } : null;
  // Any other failure (network, timeout, 5xx) must not look like "the template has no page
  // setup": Reset to template would then silently offer the defaults.
  if (!isMissingPageSetupColumn(first.error)) throw new Error(first.error.message || 'The template could not be read.');
  // The page_setup column may not exist yet on an older database: still show the template name.
  const fallback = await supabase.from('templates').select('name').eq('id', templateId).maybeSingle();
  if (fallback.error) throw new Error(fallback.error.message || 'The template could not be read.');
  return fallback.data ? { name: fallback.data.name || '', pageSetup: null } : null;
}

/**
 * @param {object} props - Props.
 * @param {object|null} props.pageSetup - Current page_setup.
 * @param {(next: object) => void} [props.onChange] - Saved from the dialog (normalized). Without it the dialog is unavailable.
 * @param {object} [props.fieldContext] - Live field values ({title, customer, project, date}).
 * @param {string|null} [props.templateId] - Template a document came from.
 * @param {string} [props.templateName] - Template name when already known.
 * @param {'document'|'template'} [props.target] - What is being edited.
 * @param {'letter'|'a4'} [props.paperSize] - Paper size (default: the stored preference).
 * @param {boolean} [props.open] - Controlled dialog open state.
 * @param {(open: boolean) => void} [props.onOpenChange] - Controlled dialog open change.
 * @param {(templateId: string) => Promise<{name: string, pageSetup: object|null}|null>} [props.loadTemplate] - Template reader (tests).
 * @param {React.ReactNode} props.children - Children.
 * @returns {JSX.Element}
 */
export function PageSetupProvider({
  pageSetup = null,
  onChange,
  fieldContext = null,
  templateId = null,
  templateName = '',
  target = 'document',
  paperSize: paperSizeProp,
  open: openProp,
  onOpenChange,
  loadTemplate = loadTemplatePageSetup,
  children,
}) {
  const [openState, setOpenState] = useState(false);
  const controlled = typeof openProp === 'boolean';
  const open = controlled ? openProp : openState;
  const setOpen = useCallback((next) => {
    if (!controlled) setOpenState(next);
    onOpenChange?.(next);
  }, [controlled, onOpenChange]);

  const [storedPaperSize, setStoredPaperSize] = useState(readPaperSize);
  useEffect(() => subscribePaperSize(setStoredPaperSize), []);
  const paperSize = paperSizeProp || storedPaperSize;

  // The template's name for "From template" (read when the dialog opens).
  const [templateInfo, setTemplateInfo] = useState(null);
  useEffect(() => {
    if (!open || !templateId || target !== 'document') return undefined;
    let cancelled = false;
    loadTemplate(templateId)
      .then((info) => {
        if (!cancelled) setTemplateInfo(info ? { id: templateId, name: info.name || templateName } : null);
      })
      .catch(() => {
        if (!cancelled && templateName) setTemplateInfo({ id: templateId, name: templateName });
      });
    return () => {
      cancelled = true;
    };
  }, [open, templateId, templateName, target, loadTemplate]);

  const loadTemplateSetup = useCallback(async () => {
    const info = await loadTemplate(templateId);
    if (!info) throw new Error('The template is no longer available.');
    return info.pageSetup ?? null;
  }, [loadTemplate, templateId]);

  const available = typeof onChange === 'function';
  const openPageSetup = useCallback(() => {
    if (available) setOpen(true);
  }, [available, setOpen]);

  const value = useMemo(
    () => ({ pageSetup, fieldContext, available, openPageSetup }),
    [pageSetup, fieldContext, available, openPageSetup]
  );

  const showTemplate = target === 'document' && Boolean(templateId);
  const template = showTemplate ? (templateInfo || (templateName ? { id: templateId, name: templateName } : null)) : null;

  return (
    <PageSetupContext.Provider value={value}>
      {children}
      {available && open && (
        <PageSetupDialog
          open
          onClose={() => setOpen(false)}
          value={pageSetup}
          onSave={onChange}
          fieldContext={fieldContext}
          paperSize={paperSize}
          template={template}
          onLoadTemplatePageSetup={template ? loadTemplateSetup : null}
          target={target}
        />
      )}
    </PageSetupContext.Provider>
  );
}

/**
 * The page setup of the document or template around this component.
 * @returns {{pageSetup: object|null, fieldContext: object|null, available: boolean, openPageSetup: () => void}|null} Context, or null outside a provider.
 */
export const usePageSetup = () => useContext(PageSetupContext);
